<template>
  <div class="page page-with-bg">
    <div class="page-form-wrap">
      <div class="page-form-header small-header">
        <h1>Initial Cardiometabolic Risk Assessment Survey</h1>
      </div>
      <horizontal-stepper class="custom-stepper"
                          ref="stepper"
                          :steps="steps"
                          :displayNumeral="true">
      </horizontal-stepper>
      <div class="page-form-footer">
        <b-form-group class="form-buttons" :class="{'form-buttons-back': currentStep > 0}">
          <b-btn class="btn-back" variant="link text-secondary" @click="goBack" v-if="currentStep > 0">Back</b-btn>
          <span>
            <b-btn variant="success" @click="next" :disabled="loading">{{ buttonText }}</b-btn>
            <b-btn v-if="!survey.isCompleted && !reportToEditId" variant="link text-secondary" @click="save" :disabled="loading">Save to complete later</b-btn>
          </span>
          <template v-if="survey.isCompleted && reportToEditId">
            <b-btn variant="success" @click="completeSurvey" :disabled="loading">Submit</b-btn>
            <b-btn class="btn-back" variant="link text-secondary" @click="cancel" >Cancel</b-btn>
          </template>
        </b-form-group>
      </div>
    </div>

    <b-modal ref="confirmSurveyCompletion"
             cancel-variant="outline-secondary"
             ok-variant="success"
             @ok="confirmSubmit"
             title="Confirm">
      <template v-if="survey.isCompleted && reportToEditId && this.survey.reportGenerated">
        This will regenerate the Cardiometabolic Risk Assessment report. Are you ready to submit your answers or do you want to go back to check?
      </template>
      <template v-else>
        This completes your Cardiometabolic Risk Assessment survey. Are you ready to submit your answers or do you want to go back to check?
      </template>
      <template #modal-footer="{ ok, cancel }">
        <b-button size="md" variant="light" @click="cancel()">
          Go Back
        </b-button>
        <b-button size="md" variant="success" @click="ok()">
          Submit
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
  import { createNamespacedHelpers } from 'vuex';
  const { mapActions: mapSurveyActions, mapState: mapSurveyState } = createNamespacedHelpers('survey');
  const { mapMutations: mapUserMutations, mapActions: mapUserActions } = createNamespacedHelpers('user');

  import genderType from 'enums/gender';
  import cholesterolLevel from "enums/cholesterol-level";
  import smokingStatus from 'enums/smoking-status';
  import billingMethodType from 'enums/billing-method';

  import { clientRole, clientUserRole, clientProviderRole, adminRole, healthQuotientClient } from "@/constants";

  import { InitialSurveySteps, lifestyleFactorSectionSteps } from './survey-steps';

  import jwtDecode from 'jwt-decode';

  export default {
    data() {
      return {
        currentStep: 0,
        steps: InitialSurveySteps,
        genderType: genderType,
        buttonText: 'Continue',
        nextButtonText: "Continue",
        payButtonText: 'Pay Now',
        loading: false
      }
    },

    computed: {
      ...mapSurveyState(["survey"]),

      reportToEditId() {
        const userAuth = jwtDecode(this.$auth.tokenData.accessToken);

        return userAuth.report_to_edit_id;
      },

      isPatientInfoHidden() {
        const userAuth = jwtDecode(this.$auth.tokenData.accessToken);

        return Boolean(userAuth.patient_info_hidden);
      }
    },

    methods: {
      ...mapSurveyActions(["update"]),
      ...mapUserMutations(["setShowProfile"]),
      ...mapUserActions(["loginBackAsAdmin"]),

      findStepIndexById(id) {
        const object = this.steps.find(step => step.id === id);

        return this.steps.indexOf(object);
      },

      setStep(index) {
        this.$refs.stepper.setStep(index);
      },

      async save(pushRoute = true) {
        await this.update();

        if (!pushRoute)
          return;

        this.$root.successToast("Survey saved!");

        let userAuth = jwtDecode(this.$auth.tokenData.accessToken);

        let loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));

        if (loggedInUser && loggedInUser.roles.some(r => r === clientRole || r === clientUserRole || r === clientProviderRole || r === adminRole) && !userAuth.patient_info_hidden) {
          await this.loginBackAsAdmin();

          this.$router.push({ path: "/reports" });

          return;
        }

        if (userAuth.patient_info_hidden) {
          this.setShowProfile(false);
          await this.$auth.logout("/");

          return;
        }

        this.$router.push("survey-incompleted");
      },

      async next() {
        this.loading = true;

        await this.$refs.stepper.next();

        this.updateNextButtonText();

        if (this.$refs.stepper.finalStep)
          await this.completeSurvey();
        else
          await this.save(false);

        this.loading = false;
      },

      completeSurvey() {
        this.$refs.confirmSurveyCompletion.show();
      },

      async confirmSubmit() {
        this.loading = true;

        const regenerateReport = Boolean(this.survey.isCompleted && Boolean(this.reportToEditId) && this.survey.reportGenerated);

        this.survey.bloodBiochemistry.cholesterol.hadTest = false;
        this.survey.bloodBiochemistry.apolipoproteinB.hadTest = false;
        this.survey.bloodBiochemistry.lipoproteinA.hadTest = false;

        this.survey.isCompleted = true;
        await this.update({ regenerateReport, cancel: false });
        this.$root.successToast("Survey completed!");

        let loggedInUser = JSON.parse(localStorage.getItem("loggedInUser"));
        let userAuth = jwtDecode(this.$auth.tokenData.accessToken);

        this.loading = false;

        if (userAuth.patient_info_hidden && !regenerateReport && !this.reportToEditId) {
          this.setShowProfile(false);
          await this.$auth.logout("/");

          return;
        }

        if (loggedInUser && loggedInUser.roles.some(r => r === clientRole || r === clientUserRole || r === clientProviderRole || r === adminRole) && (!userAuth.patient_info_hidden || regenerateReport || !this.survey.reportGenerated)) {
          await this.loginBackAsAdmin();

          this.$router.push({ path: "/reports" });

          return;
        }

        this.$router.push("survey-completed");
      },

      async cancel() {
        await this.update({ regenerateReport: false, cancel: true });

        await this.loginBackAsAdmin();

        this.$router.push({ path: "/reports" });

        return;
      },

      updateNextButtonText() {
        this.buttonText = this.$refs.stepper.currentStep.id === 'payment' ? this.payButtonText : this.nextButtonText;
      },

      goBack() {
        this.$refs.stepper.back();
        this.updateNextButtonText();
      }
    },

    watch: {
      'survey.bloodSugar.diabetesHistory.toldType1Diabetes': {
        immediate: true,
        handler(toldType1Diabetes) {
          this.steps[this.findStepIndexById('type-one-diabetes')].active = toldType1Diabetes;
        },
      },
      'survey.bloodSugar.diabetesHistory.toldType2Diabetes': {
        immediate: true,
        handler(toldType2Diabetes) {
          this.steps[this.findStepIndexById('type-two-diabetes')].active = toldType2Diabetes;
          this.steps[this.findStepIndexById('diabetes-type-two-remission')].active = toldType2Diabetes;
        }
      },

      'survey.gender': {
        immediate: true,
        handler(gender) {
          this.steps[this.findStepIndexById('pregnancy')].active = gender === genderType.Female;
          this.steps[this.findStepIndexById('female-medical-conditions')].active = gender === genderType.Female;
          this.steps[this.findStepIndexById('erectile-dysfunction')].active = gender === genderType.Male;
        },
      },

      'survey.arteryBlockages.blockageTests.hadCacTest': {
        immediate: true,
        handler(hadCacTest) {
          this.steps[this.findStepIndexById('last-cac-test')].active = hadCacTest;
        },
      },
      'survey.arteryBlockages.blockageTests.hadHeartAngiogramTest': {
        immediate: true,
        handler(hadHeartAngiogramTest) {
          this.steps[this.findStepIndexById('last-heart-angiogram')].active = hadHeartAngiogramTest;
        },
      },
      'survey.arteryBlockages.blockageTests.hadCtaTest': {
        immediate: true,
        handler(hadCtaTest) {
          this.steps[this.findStepIndexById('last-ct-angiogram')].active = hadCtaTest;
        },
      },
      'survey.arteryBlockages.blockageTests.hadCimtTest': {
        immediate: true,
        handler(hadCimtTest) {
          this.steps[this.findStepIndexById('last-cimt-test')].active = hadCimtTest;
        },
      },
      'survey.arteryBlockages.blockageTests.hadLegAngiogramTest': {
        immediate: true,
        handler(hadLegAngiogramTest) {
          this.steps[this.findStepIndexById('last-leg-angiogram')].active = hadLegAngiogramTest;
        },
      },
      'survey.arteryBlockages.blockageTests.hadAnkleBrachialTest': {
        immediate: true,
        handler(hadAnkleBrachialTest) {
          this.steps[this.findStepIndexById('last-ankle-branchial-test')].active = hadAnkleBrachialTest;
        },
      },
      'survey.arteryBlockages.blockageTests.hadCarotidAngiogramTest': {
        immediate: true,
        handler(hadCarotidAngiogramTest) {
          this.steps[this.findStepIndexById('last-angiogram-carotid-test')].active = hadCarotidAngiogramTest;
        },
      },

      'survey.arteryBlockages.arteryBlockage.hadArteryBlockage': {
        immediate: true,
        handler(hadArteryBlockage) {
          this.steps[this.findStepIndexById('artery-blockage-experience')].active = hadArteryBlockage;
          this.steps[this.findStepIndexById('stroke-experience')].active = hadArteryBlockage;
          this.steps[this.findStepIndexById('heart-attack-experience')].active = hadArteryBlockage;
          this.steps[this.findStepIndexById('heart-artery-procedures')].active = hadArteryBlockage;
          this.steps[this.findStepIndexById('leg-artery-procedures')].active = hadArteryBlockage;
          this.steps[this.findStepIndexById('neck-artery-procedures')].active = hadArteryBlockage;
          this.steps[this.findStepIndexById('cholesterol-medication-during-ascvd')].active = hadArteryBlockage;
        },
      },
      'survey.arteryBlockages.arteryBlockage.hadUnstableAngina': {
        immediate: true,
        handler(hadUnstableAngina) {
          this.steps[this.findStepIndexById('artery-symptom-dates')].active = hadUnstableAngina;
        },
      },
      'survey.arteryBlockages.strokes.hadCVA': {
        immediate: true,
        handler(hadCVA) {
          this.steps[this.findStepIndexById('stroke-dates')].active = hadCVA;
        },
      },
      'survey.arteryBlockages.heartAttacks.hadMI': {
        immediate: true,
        handler(hadMI) {
          this.steps[this.findStepIndexById('heart-attack-dates')].active = hadMI;
        },
      },
      'survey.arteryBlockages.blockedHeartArteryProcedures.hadBalloonOpening': {
        immediate: true,
        handler(hadBalloonOpening) {
          this.steps[this.findStepIndexById('heart-balloon-opening-dates')].active = hadBalloonOpening;
        },
      },
      'survey.arteryBlockages.blockedHeartArteryProcedures.hadStent': {
        immediate: true,
        handler(hadStent) {
          this.steps[this.findStepIndexById('heart-stent-dates')].active = hadStent;
        },
      },
      'survey.arteryBlockages.blockedHeartArteryProcedures.hadBypassSurgery': {
        immediate: true,
        handler(hadBypassSurgery) {
          this.steps[this.findStepIndexById('heart-bypass-surgery-dates')].active = hadBypassSurgery;
        },
      },
      'survey.arteryBlockages.blockedLegArteryProcedures.hadBalloonOpening': {
        immediate: true,
        handler(hadBalloonOpening) {
          this.steps[this.findStepIndexById('leg-ballon-opening-dates')].active = hadBalloonOpening;
        },
      },
      'survey.arteryBlockages.blockedLegArteryProcedures.hadStent': {
        immediate: true,
        handler(hadStent) {
          this.steps[this.findStepIndexById('leg-stent-dates')].active = hadStent;
        },
      },
      'survey.arteryBlockages.blockedLegArteryProcedures.hadBypassSurgery': {
        immediate: true,
        handler(hadBypassSurgery) {
          this.steps[this.findStepIndexById('leg-bypass-surgery-dates')].active = hadBypassSurgery;
        },
      },
      'survey.arteryBlockages.blockedNeckArteryProcedures.hadSurgery': {
        immediate: true,
        handler(hadSurgery) {
          this.steps[this.findStepIndexById('neck-blockage-surgery-dates')].active = hadSurgery;
        },
      },
      'survey.arteryBlockages.blockedNeckArteryProcedures.hadStent': {
        immediate: true,
        handler(hadSurgery) {
          this.steps[this.findStepIndexById('neck-stent-dates')].active = hadSurgery;
        },
      },

      'survey.bloodBiochemistry.cholesterol.highestCholesterolLevel': {
        immediate: true,
        handler(highestCholesterolLevel) {
          this.steps[this.findStepIndexById('cholesterol-range')].active = highestCholesterolLevel === cholesterolLevel.VeryHigh;
          this.steps[this.findStepIndexById('hypercholesterolemia')].active = highestCholesterolLevel === cholesterolLevel.VeryHigh;
          this.steps[this.findStepIndexById('hypercholesterolemia-diagnoses')].active = highestCholesterolLevel === cholesterolLevel.VeryHigh;
          this.steps[this.findStepIndexById('family-diagnoses')].active = highestCholesterolLevel === cholesterolLevel.VeryHigh;
        },
      },
      'survey.bloodBiochemistry.hypercholesterolemia.hadDiagnosis': {
        immediate: true,
        handler(hadDiagnosis) {
          this.steps[this.findStepIndexById('hypercholesterolemia-test')].active = hadDiagnosis;
        },
      },
      'survey.bloodBiochemistry.triglycerides.isOnTriglyceridesMedication': {
        immediate: true,
        handler(isOnTriglyceridesMedication) {
          this.steps[this.findStepIndexById('triglyceride-management')].active = isOnTriglyceridesMedication;
        },
      },
      'survey.bloodBiochemistry.cholesterol.isOnMedicationToLowerLDL': {
        immediate: true,
        handler(isOnMedicationToLowerLDL) {
          this.steps[this.findStepIndexById('cholesterol-statin')].active = isOnMedicationToLowerLDL;
        },
      },

      'survey.smoking.smokingStatus': {
        immediate: true,
        handler(status) {
          this.steps[this.findStepIndexById('smoking-currently')].active = status === smokingStatus.SmokingCurrently;
          this.steps[this.findStepIndexById('smoking-past')].active = status === smokingStatus.SmokedInPast;
        },
      },

      'survey.smoking.isSmokeless': {
        immediate: true,
        handler(isSmokeless) {
          this.steps[this.findStepIndexById('smoking-smokeless')].active = isSmokeless;
        },
      },

      'survey.medicalConditions.chronicKidneyDisease.hasCKD': {
        immediate: true,
        handler(hasCKD) {
          this.steps[this.findStepIndexById('ckd-stage')].active = hasCKD;
        },
      },

      'survey.reportInvoice.billingMethod': {
        immediate: true,
        handler(method) {
          this.steps[this.findStepIndexById('payment')].active = method === billingMethodType.IndividualBilling;
        }
      }
    },

    mounted() {
      if (this.$auth.isFeatureAvailable(healthQuotientClient)) {
        this.steps.push(...lifestyleFactorSectionSteps);
        this.survey.lifestyleFactors = {};
      }

      this.$watch(
        () => {
          this.currentStep = this.$refs.stepper?.currentStep?.index || 0;
          return this.$refs.stepper.currentStep;
        },
        (step) => {
          const stepIndex = step?.index || 0;
          this.currentStep = stepIndex;
          this.survey.lastStep = stepIndex;
        }
      );
    }
  }
</script>
<style lang="scss" scoped>
  @import '@/styles/mixins';

  .page {
    @include retina('/png/table-coffee.png');
  }
</style>